import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "../fragments/Image";
import Weather from "../fragments/Weather";
import Time from "../fragments/Time";

const PageSplash = ({ block }) => {
    const [active, setActive] = useState(0);
    const [isPortrait, setIsPortrait] = useState(false);
    const intervalRef = useRef(null);
    const videoRefs = useRef([]);

    const pageSplash = useRef(null);

    const handleResize = useCallback(() => {
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        setIsPortrait(windowHeight > windowWidth);
    }, []);

    const getMediaSrc = useCallback((slide, type = 'src') => {
        // Check if we want video or image
        const isVideo = type === 'video';
        
        // Get orientation-specific media
        const portraitMedia = isVideo ? slide.portrait_video : slide.portrait_src;
        const landscapeMedia = isVideo ? slide.landscape_video : slide.landscape_src;
        
        // Return appropriate media based on orientation, with fallbacks
        if (isPortrait) {
            return portraitMedia || landscapeMedia || slide.src;
        } else {
            return landscapeMedia || portraitMedia || slide.src;
        }
    }, [isPortrait]);

    const hasVideo = useCallback((slide) => {
        return !!(slide.portrait_video || slide.landscape_video);
    }, []);

    const playActiveVideo = useCallback(() => {
        videoRefs.current.forEach((video, index) => {
            if (video) {
                if (index === active) {
                    // Reset and play active video
                    video.currentTime = 0;
                    video.play().catch(error => {
                        console.log('Video autoplay failed:', error);
                    });
                } else {
                    // Pause inactive videos
                    video.pause();
                }
            }
        });
    }, [active]);

    useEffect(() => {
        // Set initial orientation
        handleResize();

        // Add resize listener
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        // Early return if no slides or only one slide
        if (!block?.slides || block.slides.length <= 1) return;

        // Start slideshow after initial delay
        const timeoutId = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                if (pageSplash.current) {
                    setActive(prevActive => {
                        const nextIndex = (prevActive + 1) % block.slides.length;
                        
                        // Update document class based on next slide's fitting
                        if (block.slides[nextIndex].fitting === 'inset') {
                            document.documentElement.classList.remove('state-splash-header');
                        } else {
                            document.documentElement.classList.add('state-splash-header');
                        }
                        
                        return nextIndex;
                    });
                }
            }, 4000);
        }, 2400);

        // Cleanup function
        return () => {
            clearTimeout(timeoutId);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [block?.slides?.length]);

    useEffect(() => {
        if (block.slides[0].fitting === 'inset') {
            document.documentElement.classList.remove('state-splash-header');
        } else {
            document.documentElement.classList.add('state-splash-header');
        }
    }, [block?.slides?.length]);

    // Play video when active slide changes
    useEffect(() => {
        // Add small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            playActiveVideo();
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [active, playActiveVideo]);

    // Early returns for error states
    if (!block) return null;
    if (!block.slides) return null;

    return (
        <div className="c-page__block c-page__block--splash" ref={pageSplash}>
            <div className="c-splash">
                <div className="c-splash__slides">
                    {block.slides.map((slide, i) => {
                        const videoSrc = getMediaSrc(slide, 'video');
                        const imageSrc = getMediaSrc(slide, 'src');
                        const showVideo = hasVideo(slide);

                        return (
                            <div
                                className={`c-splash__slide ${
                                    i === active ? "state-active" : ""
                                } ${slide.fitting ? `c-splash__slide--${slide.fitting}` : ''}`}
                                key={i}
                            >
                                <div className="c-splash__slide__inner">
                                    {showVideo && videoSrc ? (
                                        <video
                                            ref={el => videoRefs.current[i] = el}
                                            className="c-splash__slide__video"
                                            src={videoSrc}
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                            onCanPlay={() => {
                                                console.log(`Video ${i} can play`);
                                                if (i === active) {
                                                    playActiveVideo();
                                                }
                                            }}
                                            onError={(e) => {
                                                console.log(`Video ${i} error:`, e.target.error);
                                            }}
                                        />
                                    ) : null}
                                    
                                    {/* Always show image as fallback or primary */}
                                    <Image 
                                        src={imageSrc} 
                                        alt={slide.alt || `Slide ${i + 1}`}
                                        className={`c-splash__slide__image ${showVideo && videoSrc ? 'c-splash__slide__image--fallback' : ''}`}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <Time />
                <Weather />
            </div>
        </div>
    );
};

export default PageSplash;
